import { OfferCoupons } from "@kvix/shared";
import { Button, Text } from "@kvix/ui";
import {
  Box,
  Container as MuiContainer,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { TFunction } from "i18next";
import queryString from "query-string";
import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import styled from "styled-components";
import { KvixUserContext } from "../../../../contexts/user";
import { getAssetsPath } from "../../../../utils/getAssetsPath";
import { Parallax } from "./Parallax";

const About: React.FC<{ t: TFunction }> = (props) => (
  <Text
    variant="h1"
    color="inherit"
    style={{ fontSize: 56, marginBottom: "42px" }}
  >
    {props.t("title")}
  </Text>
);

const Offer: React.FC<{
  couponCode: string;
  anchor: React.MutableRefObject<HTMLDivElement>;
  isMobile: boolean;
}> = (props) => {
  const theme = useTheme();
  const history = useHistory();
  const { t, ready } = useTranslation("landingPageOffer");

  if (!ready) {
    return null;
  }

  return (
    <div
      style={
        !props.isMobile
          ? {
              backgroundImage: "url(/assets/about-images/offer-cover.jpg)",
              height: "864px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundSize: "cover",
            }
          : {}
      }
    >
      <Box
        padding={8}
        bgcolor={theme.palette.background.default}
        textAlign="center"
        paddingTop={props.isMobile ? 0 : 8}
      >
        <Text color="primary" variant="h5">
          {t("offerTitle")}
        </Text>
        <Text variant="h3" style={{ whiteSpace: "pre-line", margin: "22px 0" }}>
          {t("title")}
        </Text>
        <Text variant="h6" style={{ marginBottom: "12px" }}>
          {t("subtitle1")}
        </Text>
        <Text variant="h6" style={{ marginBottom: "12px" }}>
          {t("subtitle2")}
        </Text>
        <Text variant="h6" style={{ marginBottom: "38px" }}>
          {t("subtitle3")}
        </Text>
        <Text style={{ fontWeight: "bold", marginBottom: "12px" }}>
          {t("codeText", { code: props.couponCode })}
        </Text>
        <Button
          style={{
            marginBottom: "38px",
            padding: props.isMobile ? "0 48px" : "0 72px",
          }}
          onClick={() => history.push("/signup")}
        >
          {t("buttonText")}
        </Button>
        <Text
          color="primary"
          variant="h6"
          style={{ cursor: "pointer" }}
          onClick={() =>
            props.anchor.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }
        >
          {t("readMore")}
        </Text>
      </Box>
    </div>
  );
};

const BrowseClassesButton: React.FC<{ t: TFunction }> = (props) => {
  const history = useHistory();
  const { user } = useContext(KvixUserContext);
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      onClick={() => {
        if (user) {
          history.push("/");
        } else {
          history.push("signup");
        }
      }}
    >
      {props.t("general:buttons.getStarted")}
    </Button>
  );
};

const BrowseClassesButtonContainer = styled.div`
  margin: 0 auto;
`;

const Container = styled(MuiContainer)`
  display: flex;
  flex-direction: column;
  padding-top: 30px;
  padding-bottom: 60px;
`;

const ParallaxImage = styled.img`
  position: absolute;

  pointer-events: none;
  user-select: none;
  will-change: transform;
`;

const SectionMedia = styled.aside`
  position: relative;
  margin-top: -200px;
  padding-top: 70%;

  div {
    backface-visibility: hidden;
    will-change: transform;

    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const SectionText = styled.article`
  position: relative;

  h2 {
    margin-top: 8px;
  }

  p {
    font-size: 20px;
  }
`;

const Section = styled.section`
  width: 100%;
  margin: 70px 0;

  position: relative;
  display: flex;
  flex-direction: column-reverse;

  &.compact {
    margin: 30px 0;

    ${SectionMedia} {
      margin-top: 16px;
    }
  }
`;

const EveryonesWelcome = styled(Section)`
  ${SectionMedia} {
    padding-top: 76%;
    margin-top: -250px;
  }

  ${SectionText} {
    p {
      max-width: 500px;
    }
  }

  .parallax-1 {
    left: 0;
    bottom: 10%;

    width: 41%;
    max-width: 359px;

    box-shadow: 0 4px 24px -12px rgba(0, 0, 0, 0.2);
  }

  .parallax-2 {
    top: 0;
    right: 5.2%;
    bottom: 0;

    width: 50%;
    max-width: 448px;
  }

  .parallax-3 {
    top: 25%;
    right: 0;

    width: 36%;
    max-width: 320px;

    box-shadow: -2px 6px 24px -4px rgba(0, 0, 0, 0.3);
  }

  &.compact {
    .parallax-1 {
      bottom: 42%;
    }

    .parallax-3 {
      top: 17.5%;
    }
  }
`;

const YourPlaceYourWay = styled(Section)`
  ${SectionMedia} {
    margin-top: -265px;
  }

  ${SectionText} {
    margin-left: auto;

    p {
      max-width: 500px;
    }
  }

  .parallax-4 {
    left: 0;
    bottom: 0;
    height: 70%;
  }

  .parallax-5 {
    top: 0;
    left: 4%;
    bottom: 0;

    width: 31%;
    max-width: 270px;

    box-shadow: 0 4px 24px -8px rgba(0, 0, 0, 0.4);
  }

  &.compact {
    ${SectionMedia} {
      margin-top: -16px;
    }

    ${SectionText} {
      margin-left: unset;
    }

    .parallax-4 {
      top: 12.5%;
      bottom: unset;
      width: 80%;
      height: unset;
    }

    .parallax-5 {
      top: unset;
      left: unset;
      right: 5%;
      bottom: 0%;
      width: 35%;
    }
  }
`;

const RealtimeFeedback = styled(Section)`
  ${SectionMedia} {
    padding-top: 105%;
    margin-top: -365px;
  }

  ${SectionText} {
    p {
      max-width: 550px;
    }
  }

  .parallax-6 {
    left: 5%;
    bottom: 3%;
    width: 90%;
  }

  .parallax-7 {
    top: 0;
    right: 0;

    width: 31%;
    max-width: 270px;

    box-shadow: 0 6px 24px -8px rgba(0, 0, 0, 0.2);
  }

  &.compact {
    ${SectionMedia} {
      padding-top: 65%;
      margin-top: 32px;
    }

    .parallax-6 {
      top: 0;
      left: 0;
      bottom: unset;
      width: 100%;
    }

    .parallax-7 {
      display: none;
    }
  }
`;

const TrySomethingNewToday = styled(Section)`
  ${SectionMedia} {
    padding-top: 55%;
    margin-top: -300px;
  }

  ${SectionText} {
    transform: translateY(31.25%);
    margin-left: auto;

    p {
      max-width: 375px;
    }
  }

  .parallax-8 {
    top: 0;
    left: 15%;
    height: 100%;
  }

  .parallax-9 {
    top: 50%;
    left: 0;

    width: 31%;
    max-width: 270px;

    transform: translateY(-50%);
    box-shadow: 0 6px 24px -8px rgba(0, 0, 0, 0.2);
  }

  &.compact {
    ${SectionMedia} {
      padding-top: 100%;
      margin-top: 32px;
    }

    ${SectionText} {
      transform: none;

      margin-left: unset;
    }

    .parallax-8 {
      left: 30%;
      width: 70%;
      height: auto;
    }

    .parallax-9 {
      left: 0;
      width: 52.25%;
    }
  }
`;

export const ScrollArea: React.FC = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, ready } = useTranslation(["aboutUs", "general"]);
  const location = useLocation();
  const { offer } = queryString.parse(location.search);
  const couponCode = offer && OfferCoupons[`${offer.toString().toUpperCase()}`];
  const anchor = useRef<HTMLDivElement>();

  if (!ready) {
    return null;
  }

  return (
    <>
      {couponCode && (
        <Offer couponCode={couponCode} anchor={anchor} isMobile={isMobile} />
      )}
      <Container
        maxWidth="md"
        style={{ margin: couponCode ? "62px auto" : "0 auto" }}
      >
        <span ref={anchor} />
        <About t={t} />
        <EveryonesWelcome className={isMobile ? "compact" : null}>
          <SectionMedia>
            <Parallax sensitivity={isMobile ? 0.4 : 1}>
              <ParallaxImage
                className="parallax-1"
                src="/assets/about-images/1.jpg"
              />
            </Parallax>

            <Parallax sensitivity={isMobile ? 0.2 : -0.3}>
              <ParallaxImage
                className="parallax-2"
                src="/assets/about-images/2.jpg"
              />
            </Parallax>

            <Parallax sensitivity={isMobile ? 1.5 : 4}>
              <ParallaxImage
                className="parallax-3"
                src="/assets/about-images/3.jpg"
              />
            </Parallax>
          </SectionMedia>

          <SectionText>
            <Text variant="h2" gutterBottom style={{ whiteSpace: "pre-line" }}>
              {t("header1")}
            </Text>

            <Text>{t("text1")}</Text>
          </SectionText>
        </EveryonesWelcome>
        <YourPlaceYourWay className={isMobile ? "compact" : null}>
          <SectionMedia>
            <Parallax sensitivity={isMobile ? -0.1 : -1} disabled={isMobile}>
              <ParallaxImage
                className="parallax-4"
                src="/assets/about-images/4.jpg"
              />
            </Parallax>

            <Parallax sensitivity={isMobile ? 0.75 : 2}>
              <ParallaxImage
                className="parallax-5"
                src="/assets/about-images/5.jpg"
              />
            </Parallax>
          </SectionMedia>

          <SectionText>
            <Text variant="h2" gutterBottom style={{ whiteSpace: "pre-line" }}>
              {t("header2")}
            </Text>

            <Text>{t("text2")}</Text>
          </SectionText>
        </YourPlaceYourWay>
        <RealtimeFeedback className={isMobile ? "compact" : null}>
          <SectionMedia
            style={{
              ...(isMobile && { paddingTop: "50%" }),
            }}
          >
            <Parallax sensitivity={isMobile ? 0.1 : 0.3} disabled>
              <video
                style={{
                  position: "absolute",
                  pointerEvents: "none",
                  userSelect: "none",
                  willChange: "transform",
                }}
                muted
                autoPlay
                playsInline
                loop
                className="parallax-6"
              >
                <source
                  type="video/mp4"
                  src={getAssetsPath(`instructor-catch-ups.mp4`)}
                ></source>
              </video>
            </Parallax>

            <Parallax sensitivity={isMobile ? 0.5 : 2}>
              <ParallaxImage
                className="parallax-7"
                src="/assets/about-images/7.jpg"
              />
            </Parallax>
          </SectionMedia>

          <SectionText>
            <Text variant="h2" gutterBottom style={{ whiteSpace: "pre-line" }}>
              {t("header3")}
            </Text>

            <Text>{t("text3")}</Text>
          </SectionText>
        </RealtimeFeedback>
        <TrySomethingNewToday className={isMobile ? "compact" : null}>
          <SectionMedia>
            <Parallax sensitivity={isMobile ? 0.1 : 1} disabled={isMobile}>
              <ParallaxImage
                className="parallax-8"
                src="/assets/about-images/8.jpg"
              />
            </Parallax>

            <Parallax sensitivity={isMobile ? 1 : 3}>
              <ParallaxImage
                className="parallax-9"
                src="/assets/about-images/9.jpg"
              />
            </Parallax>
          </SectionMedia>

          <SectionText>
            <Text variant="h2" gutterBottom style={{ whiteSpace: "pre-line" }}>
              {t("header4")}
            </Text>

            <Text>{t("text4")}</Text>
          </SectionText>
        </TrySomethingNewToday>
        <SectionText style={{ maxWidth: "430px", marginBottom: "42px" }}>
          <Text variant="h2" gutterBottom style={{ whiteSpace: "pre-line" }}>
            {t("header5")}
          </Text>

          <Text>{t("text5")}</Text>
        </SectionText>
        <BrowseClassesButtonContainer>
          <BrowseClassesButton t={t} />
        </BrowseClassesButtonContainer>
      </Container>
      {couponCode && (
        <Offer couponCode={couponCode} anchor={anchor} isMobile={isMobile} />
      )}
    </>
  );
};
